import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { FiX } from "react-icons/fi";

const Drawer = ({
  isOpen,
  onClose,
  title,
  children,
  panelClassName = "",
  zIndex = 1200,
}) => {
  const titleId = useId();
  const panelRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!isOpen || typeof document === "undefined") return undefined;

    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }

      if (event.key !== "Tab" || !panelRef.current) return;
      const focusableElements = panelRef.current.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement?.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      previousFocus?.focus?.();
    };
  }, [isOpen, onClose]);

  if (!isOpen || typeof document === "undefined") return null;

  return createPortal(
    <div className="tw-fixed tw-inset-0" style={{ zIndex }}>
      <button
        type="button"
        className="tw-absolute tw-inset-0 tw-h-full tw-w-full tw-cursor-default tw-border-0 tw-bg-dis-scrim tw-p-0 tw-animate-connect-fade"
        onClick={onClose}
        aria-label="Close help drawer"
      />
      <section
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`tw-absolute tw-right-0 tw-top-0 tw-flex tw-h-full tw-w-full tw-max-w-[520px] tw-flex-col tw-bg-white tw-font-normal tw-text-dis-base tw-leading-[1.5] tw-text-dis-text-2 tw-shadow-dis-popup tw-animate-drawer-in-right max-[640px]:tw-bottom-0 max-[640px]:tw-left-0 max-[640px]:tw-right-0 max-[640px]:tw-top-auto max-[640px]:tw-h-auto max-[640px]:tw-max-h-[90vh] max-[640px]:tw-max-w-none max-[640px]:tw-rounded-t-2xl max-[640px]:tw-animate-drawer-in-bottom ${panelClassName}`}
      >
        <header className="tw-relative tw-flex tw-min-h-[50px] tw-shrink-0 tw-items-center tw-justify-center tw-border-x-0 tw-border-t-0 tw-border-b tw-border-solid tw-border-dis-border tw-px-[54px] tw-py-1 md:tw-min-h-[60px] md:tw-px-16 md:tw-py-1.5">
          <h2
            id={titleId}
            className="tw-m-0 tw-text-center tw-text-lg tw-font-extrabold tw-leading-[1.2] tw-tracking-[-0.01em] tw-text-dis-navy md:tw-text-[22px]"
          >
            {title}
          </h2>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="tw-absolute tw-right-3 tw-top-0 tw-bottom-0 tw-my-auto tw-inline-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-white tw-p-0 tw-text-dis-navy hover:tw-bg-dis-sky-tint focus-visible:tw-outline-2 focus-visible:tw-outline-dis-deep-blue focus-visible:tw-outline-offset-2 tw-cursor-pointer"
            aria-label="Close drawer"
          >
            <FiX className="tw-h-6 tw-w-6" aria-hidden="true" />
          </button>
        </header>
        <div className="tw-no-scrollbar tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-px-6 tw-py-5 max-[640px]:tw-px-5 max-[640px]:tw-pb-7">
          {children}
        </div>
      </section>
    </div>,
    document.body,
  );
};

export default Drawer;
